import { apiSlice } from 'src/features/api/api-slice';
import { RTKQueryTag } from 'src/common/constants';
import { ApiResponse } from 'src/common/interfaces';
import {
  FetchCategoriesQueryParams,
  GenerateRandomAvatarResponse,
  AccessMember,
  GetRoomCategoriesQueryParams,
  ManageRoomCategory,
  GetUsersQueryParams,
  UsersResponse,
  StatsSummary,
  StatsScore,
  Task,
  TaskMaterial,
  TaskImagePresignedUrlData,
  Question,
} from 'src/common/interfaces/manage-room';
import { store } from 'src/app/store';
import { RoomWeightedTag, Tag } from 'src/features/admin';
import { decode } from 'src/common/helpers';

import { UpdatedRoomPayload } from './components/general-settings/general-settings.types';
import {
  ClonedRoomResponse,
  RoomCloud,
  CommunityWriteUpsParams,
  DataResponse,
  ManageRoomDetail,
} from './manage-room.types';
import { DesignUpdatePayload } from './components/design-tab/design-tab.types';
import {
  DeleteWriteUpPayload,
  UpdateForumThreadsPayload,
  UpdateUserWriteUpsPayload,
  UpdateWriteUpsStatus,
} from './components/write-ups/write-ups.types';

export const manageRoomApiSlice = apiSlice.injectEndpoints({
  endpoints: (builder) => ({
    getManageRoomDetail: builder.query<ApiResponse<ManageRoomDetail>, string>({
      query: (roomCode: string) => ({
        url: 'rooms/manage/details',
        params: {
          roomCode,
        },
      }),
      providesTags: (result, error, args) => [{ type: RTKQueryTag.ManageRoomDetail, id: args }],
    }),

    updateRoom: builder.mutation({
      query: ({ roomCode, payload }: { roomCode: string; payload: UpdatedRoomPayload }) => ({
        url: 'rooms/manage/details',
        params: {
          roomCode,
        },
        method: 'PATCH',
        body: payload,
      }),
      invalidatesTags: (result, error, args) =>
        !error && result ? [{ type: RTKQueryTag.ManageRoomDetail, id: args.payload.code }] : [],
    }),

    updateCloudIntegration: builder.mutation({
      query: ({ roomCode, payload }: { roomCode: string; payload: Partial<RoomCloud> }) => ({
        url: 'rooms/manage/cloud',
        params: {
          roomCode,
        },
        method: 'PATCH',
        body: payload,
      }),
      invalidatesTags: (result, error, args) =>
        !error && result
          ? [
              { type: RTKQueryTag.ManageRoomTasks, id: args.roomCode },
              { type: RTKQueryTag.ManageRoomDetail, id: args.roomCode },
              { type: RTKQueryTag.ManageRoomTaskMaterial },
            ]
          : [],
    }),

    generateRandomRoomAvatar: builder.mutation<ApiResponse<GenerateRandomAvatarResponse>, { roomCode: string }>({
      query: ({ roomCode }) => ({
        url: `rooms/manage/avatar/random`,
        params: {
          roomCode,
        },
      }),
    }),

    updateRoomDesign: builder.mutation({
      query: ({ roomCode, payload }: { roomCode: string; payload: DesignUpdatePayload }) => ({
        url: `rooms/manage/design`,
        params: {
          roomCode,
        },
        method: 'PATCH',
        body: payload,
      }),

      invalidatesTags: (result, error, args) =>
        !error && result ? [{ type: RTKQueryTag.ManageRoomDetail, id: args.roomCode }] : [],
    }),

    updateUserWritesUps: builder.mutation({
      query: (payload: UpdateUserWriteUpsPayload) => ({
        url: `rooms/manage/writeups/official?roomCode=${payload.code}`,
        method: 'PUT',
        body: { url: payload.url },
      }),

      invalidatesTags: (result, error, args) =>
        !error && result ? [{ type: RTKQueryTag.ManageRoomDetail, id: args.code }] : [],
    }),

    updateForumThread: builder.mutation({
      query: (payload: UpdateForumThreadsPayload) => ({
        url: `rooms/manage/forum-link`,
        method: 'PUT',
        body: { ...payload, roomCode: payload.code },
      }),

      invalidatesTags: (result, error, args) =>
        !error && result ? [{ type: RTKQueryTag.ManageRoomDetail, id: args.code }] : [],
    }),

    fetchCategories: builder.query<ApiResponse<ManageRoomCategory[]>, FetchCategoriesQueryParams>({
      query: ({ searchText, limit, roomCode }: FetchCategoriesQueryParams) => ({
        url: 'categories',
        params: {
          roomCode,
          search: searchText,
          limit,
        },
      }),
      providesTags: (result, error, args) => [{ type: RTKQueryTag.ManageCategories, id: args.roomCode }],
    }),

    getRoomTags: builder.query<Tag[], GetRoomCategoriesQueryParams>({
      query: ({ roomCode }: GetRoomCategoriesQueryParams) => ({
        url: 'rooms/manage/tags',
        params: {
          roomCode,
        },
      }),
      providesTags: [RTKQueryTag.ManageRoomTags],
      transformResponse: (response: ApiResponse<Tag[]>) => response.data || [],
    }),

    getRoomCategories: builder.query<ApiResponse<ManageRoomCategory[]>, GetRoomCategoriesQueryParams>({
      query: ({ roomCode }: GetRoomCategoriesQueryParams) => ({
        url: 'rooms/manage/categories',
        params: {
          roomCode,
        },
      }),
      providesTags: (result, error, args) => [{ type: RTKQueryTag.ManageRoomCategories, id: args.roomCode }],
    }),

    updateRoomTagIds: builder.mutation({
      query: ({ roomCode, payload }: { roomCode: string; payload: string[] | RoomWeightedTag[] }) => ({
        url: 'rooms/manage/tags',
        params: {
          roomCode,
        },
        method: 'PUT',
        body: payload,
      }),
      invalidatesTags: [RTKQueryTag.ManageRoomTags],
    }),

    updateCategories: builder.mutation({
      query: ({ roomCode, payload }: { roomCode: string; payload: ManageRoomCategory[] }) => ({
        url: 'rooms/manage/categories',
        params: {
          roomCode,
        },
        method: 'PUT',
        body: payload,
      }),

      invalidatesTags: (result, error, args) =>
        !error && result
          ? [
              { type: RTKQueryTag.ManageRoomCategories, id: args.roomCode },
              { type: RTKQueryTag.ManageCategories, id: args.roomCode },
            ]
          : [],
    }),

    getRoomUsers: builder.query<UsersResponse, GetUsersQueryParams>({
      query: ({ roomId, page, limit, sortOrder, searchText }: GetUsersQueryParams) => ({
        url: 'rooms/manage/users',
        params: {
          roomId,
          page,
          limit,
          sortOrder,
          searchText,
        },
      }),
      providesTags: (result, error, args) => [{ type: RTKQueryTag.ManageRoomUsers, id: args.roomId }],
    }),

    banManageRoomUser: builder.mutation({
      query: (payload: { roomId: string; userId: string }) => ({
        url: 'rooms/manage/users/ban',
        method: 'POST',
        body: payload,
      }),
      invalidatesTags: (result, error, args) =>
        !error && result ? [{ type: RTKQueryTag.ManageRoomUsers, id: args.roomId }] : [],
    }),

    resetManageRoomUserProgress: builder.mutation({
      query: (payload: { roomId: string; userId: string }) => ({
        url: 'rooms/manage/users/reset-progress',
        method: 'POST',
        body: payload,
      }),
      invalidatesTags: (result, error, args) =>
        !error && result ? [{ type: RTKQueryTag.ManageRoomUsers, id: args.roomId }] : [],
    }),

    getStatsSummary: builder.query<StatsSummary, string>({
      query: (roomCode: string) => ({
        url: 'rooms/manage/stats/summary',
        params: {
          roomCode,
        },
      }),
      transformResponse: (response: ApiResponse<StatsSummary>) => response?.data as StatsSummary,
    }),

    getStatsScoreboard: builder.query<StatsScore[], string>({
      query: (roomCode: string) => ({
        url: 'rooms/manage/scoreboard',
        params: {
          roomCode,
          limit: 500,
        },
      }),
      providesTags: (result, error, args) => [{ type: RTKQueryTag.ManageRoomStatsScoreboard, id: args }],
      transformResponse: (response: ApiResponse<StatsScore[]>) => response?.data as StatsScore[],
    }),

    getManageTaskMaterial: builder.query<Record<string, TaskMaterial[]>, string>({
      query: (roomId: string) => ({
        url: 'rooms/manage/material',
        params: {
          roomId,
        },
      }),
      transformResponse: (response: ApiResponse<Record<string, TaskMaterial>>) =>
        response?.data as unknown as Record<string, TaskMaterial[]>,
      providesTags: [RTKQueryTag.ManageRoomTaskMaterial],
    }),

    getManageRoomTasks: builder.query<Task[], string>({
      query: (roomCode: string) => ({
        url: 'rooms/manage/tasks',
        params: {
          roomCode,
        },
      }),
      transformResponse: (response: ApiResponse<Task[]>) =>
        response?.data
          ?.map((t: Task) => ({
            ...t,
            infoList: t.infoList.map((q: Question) => ({
              ...q,
              answer: decode(q.answer),
              hint: decode(q.hint),
            })),
          }))
          .sort((a, b) => a.taskNo - b.taskNo) ?? [],
      providesTags: (result, error, args) => [{ type: RTKQueryTag.ManageRoomTasks, id: args }],
    }),

    saveTask: builder.mutation({
      query: (payload: Task) => ({
        url: 'rooms/manage/task',
        method: 'POST',
        body: payload,
      }),
      invalidatesTags: (result, error, args) =>
        !error && result
          ? [
              { type: RTKQueryTag.ManageRoomTasks, id: args.roomCode },
              { type: RTKQueryTag.ManageRoomStatsScoreboard, id: args.roomCode },
            ]
          : [],
    }),

    deleteManageRoomTask: builder.mutation({
      query: ({ roomCode, taskId }: { roomCode: string; taskId: string }) => ({
        url: 'rooms/manage/task',
        method: 'DELETE',
        params: {
          roomCode,
          taskId,
        },
      }),
      invalidatesTags: (result, error, args) =>
        !error && result ? [{ type: RTKQueryTag.ManageRoomTasks, id: args.roomCode }] : [],
    }),

    updateManageRoomTasksOrder: builder.mutation({
      query: (payload: { roomCode: string; tasks: { taskId: string; order: number }[] }) => ({
        url: 'rooms/manage/tasks/update-order',
        method: 'PUT',
        body: payload,
      }),
      invalidatesTags: (result, error, args) =>
        !error && result ? [{ type: RTKQueryTag.ManageRoomTasks, id: args.roomCode }] : [],
    }),

    getAccessMembers: builder.query<ApiResponse<AccessMember[]>, string>({
      query: (roomId: string) => ({
        url: 'rooms/manage/access',
        params: {
          roomId,
        },
      }),
      providesTags: [RTKQueryTag.ManageRoomAccessMembers],
    }),

    deleteRoom: builder.mutation({
      query: ({ roomCode }: { roomCode: string }) => ({
        url: 'rooms/manage',
        params: {
          roomCode,
        },
        method: 'DELETE',
      }),
      invalidatesTags: [RTKQueryTag.ManageRooms],
    }),

    getCommunityWriteUps: builder.query<ApiResponse<DataResponse>, CommunityWriteUpsParams>({
      query: ({ page, limit, searchText = '', roomCode }) =>
        `rooms/manage/writeups/list?roomCode=${roomCode}&page=${page}&limit=${limit}&searchText=${searchText}`,
      providesTags: [RTKQueryTag.ManageUserWriteUps],
    }),

    updateWriteUpsStatus: builder.mutation({
      query: (payload: UpdateWriteUpsStatus) => ({
        url: 'rooms/manage/writeups/status',
        method: 'PUT',
        body: payload,
      }),
      invalidatesTags: [{ type: RTKQueryTag.ManageUserWriteUps }],
    }),

    deleteWriteUp: builder.mutation({
      query: (payload: DeleteWriteUpPayload) => ({
        url: 'rooms/manage/writeups',
        method: 'DELETE',
        body: payload,
      }),
      invalidatesTags: [{ type: RTKQueryTag.ManageUserWriteUps }],
    }),

    cloneRoom: builder.mutation<ApiResponse<ClonedRoomResponse>, string>({
      query: (roomId: string) => ({
        url: 'rooms/manage/clone',
        method: 'POST',
        params: {
          roomId,
        },
      }),
      invalidatesTags: [RTKQueryTag.ManageRooms],
    }),

    getTaskImagePresignedUrl: builder.mutation<TaskImagePresignedUrlData, { roomId: string; fileName: string }>({
      query: ({ roomId, fileName }: { roomId: string; fileName: string }) => ({
        url: 'rooms/manage/images/pre-signed-url',
        method: 'GET',
        params: {
          roomId,
          fileName,
        },
      }),
      transformResponse: (response: ApiResponse<TaskImagePresignedUrlData>) =>
        response?.data as TaskImagePresignedUrlData,
    }),

    uploadTaskImagesToS3: builder.mutation({
      query: ({ file, url }: { file: File; url: string }) => ({
        url,
        method: 'PUT',
        headers: {
          'Content-Type': file.type,
        },
        body: file,
      }),
    }),

    saveTaskImage: builder.mutation<string[], { roomId: string; files: string[] }>({
      query: (payload: { roomId: string; files: string[] }) => ({
        url: 'rooms/manage/images/save',
        method: 'POST',
        body: payload,
      }),
      transformResponse: (response: ApiResponse<string[]>) => response?.data as string[],
    }),

    removeTaskImages: builder.mutation({
      query: (payload: { roomId: string; files: string[] }) => ({
        url: 'rooms/manage/images/remove',
        method: 'POST',
        body: payload,
      }),
    }),

    resetRoom: builder.mutation({
      query: ({ roomCode }: { roomCode: string }) => ({
        url: 'rooms/manage/reset',
        params: {
          roomCode,
        },
        method: 'POST',
      }),
      invalidatesTags: (result, error, args) =>
        !error && result ? [{ type: RTKQueryTag.ManageRoomDetail, id: args.roomCode }] : [],
    }),
  }),
});

export const invalidateCachedCategories = (roomCode: string) => {
  store.dispatch(
    manageRoomApiSlice.util.invalidateTags([
      { type: RTKQueryTag.ManageCategories, id: roomCode },
      { type: RTKQueryTag.ManageRoomCategories, id: roomCode },
    ]),
  );
};

export const {
  useGetCommunityWriteUpsQuery,
  useGetManageRoomDetailQuery,
  useUpdateRoomMutation,
  useUpdateCloudIntegrationMutation,
  useGenerateRandomRoomAvatarMutation,
  useUpdateRoomDesignMutation,
  useUpdateUserWritesUpsMutation,
  useUpdateForumThreadMutation,
  useUpdateWriteUpsStatusMutation,
  useDeleteWriteUpMutation,
  useFetchCategoriesQuery,
  useGetRoomTagsQuery,
  useUpdateRoomTagIdsMutation,
  useGetAccessMembersQuery,
  useGetRoomCategoriesQuery,
  useUpdateCategoriesMutation,
  useGetRoomUsersQuery,
  useBanManageRoomUserMutation,
  useResetManageRoomUserProgressMutation,
  useGetStatsSummaryQuery,
  useGetStatsScoreboardQuery,
  useGetManageTaskMaterialQuery,
  useGetManageRoomTasksQuery,
  useSaveTaskMutation,
  useDeleteManageRoomTaskMutation,
  useDeleteRoomMutation,
  useUpdateManageRoomTasksOrderMutation,
  useCloneRoomMutation,
  useGetTaskImagePresignedUrlMutation,
  useUploadTaskImagesToS3Mutation,
  useSaveTaskImageMutation,
  useRemoveTaskImagesMutation,
  useResetRoomMutation,
} = manageRoomApiSlice;
